import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Eye, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { contactFormsAPI, type ContactForm } from "../../services/api";
import { formatDate } from "../../lib/utils";

export default function ContactQueries() {
  const [contacts, setContacts] = useState<ContactForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactForm | null>(
    null
  );
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await contactFormsAPI.list();
        if (!cancelled) {
          setContacts(list);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load contact submissions");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleViewContact = async (contact: ContactForm) => {
    setSelectedId(contact.id);
    setSelectedContact(null);
    setReplyText("");
    setLoadingDetail(true);
    try {
      const detail = await contactFormsAPI.getById(contact.id);
      setSelectedContact(detail ?? contact);
    } catch {
      toast.error("Failed to load contact details");
      setSelectedContact(contact);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReply = async () => {
    if (
      selectedId === null ||
      !selectedContact?.email?.trim() ||
      !replyText.trim()
    ) {
      return;
    }
    setSendingReply(true);
    try {
      await contactFormsAPI.sendMail(selectedId, {
        email: selectedContact.email.trim(),
        response: replyText.trim(),
      });
      toast.success("Reply sent successfully!");
      handleCloseDialog();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; detail?: string } };
        message?: string;
      };
      const message =
        ax?.response?.data?.message ??
        ax?.response?.data?.detail ??
        ax?.message ??
        "Failed to send reply. Please try again.";
      toast.error(message);
      // Keep modal open on failure so user can fix and retry
    } finally {
      setSendingReply(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedId(null);
    setSelectedContact(null);
    setReplyText("");
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Contact Queries</h1>
        <p className="text-muted-foreground">
          Manage all contact form submissions and inquiries
        </p>
      </div>

      {/* Contact Queries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            All Contact Submissions ({contacts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sr. No</TableHead>
                  <TableHead>Contact Person Name</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Email ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground"
                    >
                      No contact submissions yet
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((contact, index) => (
                    <TableRow
                      key={contact.id}
                      className="cursor-pointer hover:bg-gray-100"
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="font-medium">
                        {contact.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {contact.subject || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell>{contact.phone_number || "—"}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{formatDate(contact.created_at)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            contact.status === "new"
                              ? "destructive"
                              : contact.status === "in_progress"
                                ? "secondary"
                                : "default"
                          }
                          className={
                            contact.status === "responded"
                              ? "bg-primary"
                              : "text-white"
                          }
                        >
                          {(contact.status ?? "new").replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewContact(contact)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog open={selectedId !== null} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Query Details</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            selectedContact && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Person</Label>
                    <p className="font-medium text-foreground">
                      {selectedContact.name}
                    </p>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Badge variant="outline">
                      {selectedContact.subject || "—"}
                    </Badge>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <p className="text-foreground">
                      {selectedContact.phone_number || "—"}
                    </p>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <p className="text-foreground">{selectedContact.email}</p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <p className="text-foreground">
                      {formatDate(selectedContact.created_at)}
                    </p>
                  </div>
                  {selectedContact.status && (
                    <div>
                      <Label>Status</Label>
                      <Badge variant="secondary">
                        {selectedContact.status.replace("_", " ")}
                      </Badge>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Message</Label>
                  <div className="mt-2 rounded-lg bg-accent p-4">
                    <p className="whitespace-pre-wrap text-white">
                      {selectedContact.message}
                    </p>
                  </div>
                </div>

                {selectedContact.status === "responded" && (
                  <div>
                    <Label>Previous Response</Label>
                    <div className="mt-2 rounded-lg border border-primary/20 bg-primary/10 p-4">
                      <p className="text-sm text-foreground">
                        {selectedContact.response?.trim() ??
                          "Thank you for reaching out. We have received your inquiry and will get back to you shortly."}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="response">Response</Label>
                  <Textarea
                    id="response"
                    placeholder="Type your response..."
                    rows={5}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCloseDialog}
                    disabled={sendingReply}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReply}
                    disabled={!replyText.trim() || sendingReply}
                  >
                    {sendingReply ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reply"
                    )}
                  </Button>
                </div>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
